// @ts-strict-ignore
import type { NodeLineage } from '../types';
import type { Graph } from '../../types';
import type {
  OutcomeNode, WorkflowNode, WorkflowRoutingNode,
} from '../../nodes/types';
import type { WorkflowState } from '~/dashboard/views/workflows/store/redux/state';
import { workflowNodeType, getNodeLabel } from '../../nodes';
import { typeSortOrder } from '../constants';


/**
 * Sort island nodes based on their precedence index
 * @param nodes with island nodes sorted in DESC order
 */
export function sortIslandNodesByPrecedenceIndex(nodes: WorkflowNode[]) {
  type IslandNodeData = OutcomeNode['data'] | WorkflowRoutingNode['data'];
  nodes.sort((nodeA, nodeB) => {
    // compare precedenceIndexes if both items are island nodes
    if ((nodeA.type === 'outcome' && nodeB.type === 'outcome')
      || (nodeA.type === 'workflow' && nodeB.type === 'workflow')
    ) {
      return (nodeB.data.precedenceIndex) - (nodeA.data.precedenceIndex);
    }

    // use the precedenceIndex from the one island node (we don't know which though)
    if ((nodeA.type === 'outcome' || nodeB.type === 'outcome')
      || (nodeA.type === 'workflow' || nodeB.type === 'workflow')
    ) {
      return (
        ((nodeB.data as IslandNodeData)?.precedenceIndex ?? Infinity)
        - ((nodeA.data as IslandNodeData)?.precedenceIndex ?? Infinity)
      );
    }

    // neither item is an island node so leave don't change the order
    return 0;
  });
}

/**
 *
 * @param nodes array of nodes
 * @param floatedAnchorNodeId node that was selected to float by the user
 * @param floatedNodeIdsSet ids of all nodes connected (ancestors and descendants) to the
 * floatedAnchorNode
 */
export function sortNodesForFloating(
  nodes: WorkflowNode[], floatedAnchorNodeId: WorkflowNode['id'], nodeLineage: NodeLineage,
) {
  const {
    all,
    directIncomers,
  } = nodeLineage;

  /**
   * group all nodes connected to the anchor at the beginning of the array
   * this will naturally "float" up all floated nodes once they are placed by the positional
   * algorithm since they will be evaluated first
   */
  nodes.sort((a, b) => {
    // avoid floating nodes that opted out of this functionality
    if (!a.data?.layout.float || !b.data?.layout.float) return 0;
    if (a.id === floatedAnchorNodeId) return -1;
    if (directIncomers.has(a.id)) return -1;
    if (all.has(a.id) && all.has(b.id)) return 0;
    if (all.has(a.id)) return -1;
    return 1;
  });
}

/**
 * Sorts folder and child such they occur at the beginning of a list,
 * with the folder preceding its children. Currently only used for
 * input attributes, and since input attributes never depend on anything else,
 * they will always populate the leftmost column.
 *
 * @param nodes array of nodes
*/
export function sortFolderChildren(nodes: WorkflowNode[]) {
  /**
  * sorting rules:
  * place folder nodes before all other nodes
  * place child after folder & before all other nodes
  */
  const childType = workflowNodeType.inputAttribute;
  nodes.sort((a, b) => {
    switch (a.type) {
      case workflowNodeType.folder: {
        return -1;
      }
      case childType: {
        if (b.type === childType) {
          return 0;
        }
        return -1;
      }
      default: {
        if (
          b.type === workflowNodeType.folder
          || b.type === childType
        ) {
          return 1;
        }
        return 0;
      }
    }
  });
}

/**
 * @param nodes array of nodes
 */
export function sortNodesByTypeAndLabel(nodes: WorkflowNode[]) {
  nodes.sort((a, b) => {
    // compare by type
    const orderA = typeSortOrder[a.type];
    const orderB = typeSortOrder[b.type];
    if (orderA < orderB) return -1;
    if (orderA > orderB) return 1;

    // if types are equal, compare by label alphabetically
    const labelA = getNodeLabel(a).toLowerCase();
    const labelB = getNodeLabel(b).toLowerCase();
    if (labelA < labelB) return -1;
    if (labelA > labelB) return 1;

    return 0;
  });
}

/**
 * Sorts the nodes before they are laid out in the graphical interface.
 *
 * All nodes are first sorted by their type and label to ensure a consistent order in the editor.
 *
 * Island nodes are sorted by their precedence index.
 * Sorts nodes to be floated if needed - nodes connected to the floated anchor node are grouped at
 * the beginning of the node array and other at the end.
 *
 * Sorting prior to laying out nodes in the graph helps keep the positional algorithm simple
 * and readable, as what determines where the nodes are placed is the order in which they are
 * evaluated
 *
 * @param graph graph containing nodes and edges
 * @param uiStatePartial state of the UI components and interactions
 * @param nodeLineage bundle of node connections
 */
export function sortGraphBeforePlacement(
  graph: Graph,
  uiStatePartial: {
    floatedAnchorNodeId: WorkflowState['ui']['floatedAnchorNodeId'];
  },
  nodeLineage: NodeLineage,
  featureFlags: {
    sortWorkflowNodesByType: boolean;
  },
) {
  const {
    floatedAnchorNodeId,
  } = uiStatePartial;

  if (featureFlags.sortWorkflowNodesByType) {
    sortNodesByTypeAndLabel(graph.nodes);
  } else {
    /**
     * This is necessary to ensure collapsible input attributes still function correctly if the Sort Workflow feature flag is disabled
     * sortNodesByTypeAndLabel now includes the input attributes/folder sorting logic
     * sortFolderChildren can be safely deleted once the Sort Workflow feature flag is removed
     */
    sortFolderChildren(graph.nodes);
  }

  sortIslandNodesByPrecedenceIndex(graph.nodes);

  if (floatedAnchorNodeId) {
    sortNodesForFloating(graph.nodes, floatedAnchorNodeId, nodeLineage);
  }
}
